import { find } from "ramda";
import { type FC, useCallback, useEffect, useState } from "react";
import type { FormatOptionLabelMeta } from "react-select";
import Select from "react-select";

import { AlertDialog, HStack, TriggerButton, TriggerButtonIntent, TriggerButtonSize } from "widgets";
import { useControlledDialog } from "widgets/hooks";

import { type Template } from "../../../graphql.js";
import { type IColumnNode } from "./ColumnSelectionDialog.js";

export interface ColumnTemplateManagerProps {
  isLoading: boolean;
  columnTemplates: Template[];
  columns: IColumnNode[];

  setColumnTemplateAsSelected: (columnTemplate: Template) => void;
  deleteTemplate: (columnTemplate: Template) => Promise<boolean>;
  checkSelectedColumnsFitSelectedTemplate: (selectedTemplate: Template) => boolean;
}

const ColumnTemplateManager: FC<ColumnTemplateManagerProps> = (props) => {
  const getCurrentTemplateSelection = useCallback(
    (columnTemplates: Template[]): Template | null => {
      const selectedTemplate: Template | undefined = find(
        (columnTemplate) => !!columnTemplate.selected && !columnTemplate.active,
        columnTemplates,
      );

      // the first check is required in multi-tab environments (see SEL6-367)
      if (selectedTemplate && props.checkSelectedColumnsFitSelectedTemplate(selectedTemplate)) {
        return selectedTemplate;
      } else {
        return null;
      }
    },
    [props],
  );

  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null);
  const [currentTemplateSelection, setCurrentTemplateSelection] = useState<Template | null>(
    getCurrentTemplateSelection(props.columnTemplates),
  );

  useEffect(() => {
    setCurrentTemplateSelection(getCurrentTemplateSelection(props.columnTemplates));
  }, [getCurrentTemplateSelection, props.columnTemplates, props.columns]);

  const onChange = (option: Template) => {
    setCurrentTemplateSelection(option);
    props.setColumnTemplateAsSelected(option);
  };

  const confirmTemplateDeletion = () => {
    return templateToDelete ? props.deleteTemplate(templateToDelete) : Promise.resolve(false);
  };

  const columnTemplateOptionRenderer = (option: Template, labelMeta: FormatOptionLabelMeta<Template>) => {
    const deletable = !option.default;

    return (
      <HStack>
        <span className={"standard-text-ellipsis"} style={{ marginRight: "auto" }}>
          {option.name}
        </span>
        {labelMeta.context === "menu" && (
          <TriggerButton
            icon={"delete"}
            label={"Delete"}
            intent={TriggerButtonIntent.Danger}
            onPressStart={() => {
              deletable && setTemplateToDelete(option);
            }}
            isDisabled={deletable ? false : "Cannot delete the immutable default template."}
            size={TriggerButtonSize.Small}
          />
        )}
      </HStack>
    );
  };

  return (
    <>
      <Select
        className={"react-select template-selector"}
        classNamePrefix={"react-select"}
        placeholder={"Templates"}
        isMulti={false}
        value={currentTemplateSelection}
        isClearable={false}
        isSearchable={true}
        isLoading={props.isLoading}
        options={props.columnTemplates.filter((t: Template) => !t.active)}
        formatOptionLabel={columnTemplateOptionRenderer}
        getOptionValue={(option: Template) => option.id}
        getOptionLabel={(option: Template) => option.name} // required for search functionality
        onChange={onChange}
        closeMenuOnSelect={true}
        openMenuOnFocus={true}
        styles={{
          control: (baseStyles) => ({
            ...baseStyles,
            width: 180,
          }),
        }}
      />
      {templateToDelete && (
        <DeleteTempleDialog
          onDelete={() => confirmTemplateDeletion()}
          onCancel={() => {
            setTemplateToDelete(null);
          }}
          templateToDelete={templateToDelete}
        />
      )}
    </>
  );
};

interface DeleteTempleDialogProps {
  templateToDelete: Template;
  onCancel: () => void;
  onDelete: () => Promise<boolean>;
}

const DeleteTempleDialog: FC<DeleteTempleDialogProps> = (props) => {
  const { dialogProps, dialogRef, underlayProps } = useControlledDialog(props.onCancel);

  return (
    <AlertDialog
      ref={dialogRef}
      domProps={dialogProps}
      underlayProps={underlayProps}
      title="Delete Template"
      closeFn={props.onCancel}
      mainButton={{
        label: "Delete",
        onPress: () => props.onDelete().then(props.onCancel),
      }}
    >
      Are you sure you want to delete the template <strong>&#39;{props.templateToDelete.name}&#39;</strong>?
    </AlertDialog>
  );
};

export default ColumnTemplateManager;
